import { parseColorToRgba, toRgbString } from 'utils/color';

import { getAllTextBasedWidgetColors } from '../common/TextBasedWidgets.helpers';
import { Effect } from './TextWidget.types';
import { DEFAULT_EFFECT } from './defaultTextData';

// adjust this to increase the shadow offset - by trial and error 600 seems to be a good value
const SHADOW_DIVISOR = 600;

/**
 * Calculates the blur for a shadow based on the font size and offset percentage.
 *
 * The function takes in the font size of the text and the desired offset as a percentage,
 * and returns the corresponding blur in pixels.
 *
 * @param {number} fontSize - The size of the font in pixels.
 * @param {number} offsetPercentage - The desired offset as a percentage.
 *
 * @returns {number} The blur for the shadow in pixels.
 */
const calculateShadowBlur = (fontSize: number, offsetPercentage: number) => {
  // Calculate the actual offset in pixels
  const blurPx = (fontSize * offsetPercentage) / SHADOW_DIVISOR;

  return blurPx;
};

/**
 * Calculates the x and y offset for the shadow based on the font size, offset percentage, and direction.
 *
 * The function takes in the font size of the text, the desired offset as a percentage, and the direction
 * (in degrees, where 0 degrees is up), and returns the x and y offset for the shadow.
 *
 * @param {number} fontSize - The size of the font in pixels.
 * @param {number} offsetPercentage - The desired offset as a percentage.
 * @param {number} direction - The direction of the shadow in degrees. 0 degrees corresponds to 'up'.
 *
 * @returns {Object} An object containing the x and y offsets for the shadow.
 */
const calculateShadowDirection = (fontSize: number, offsetPercentage: number, direction: number) => {
  // Calculate the actual offset in pixels
  const offset = (fontSize * offsetPercentage) / SHADOW_DIVISOR;

  // Subtract 90 from the direction to make 0 degrees correspond to 'up'
  direction -= 90;

  // Convert the direction to radians
  const angleRadians = (direction * Math.PI) / 180;

  // Calculate the x and y offsets based on the direction (angle in degrees)
  const xOffset = offset * Math.cos(angleRadians);
  const yOffset = offset * Math.sin(angleRadians);

  return { xOffset, yOffset };
};

/**
 * Calculates the thickness of the outline for a 'hollow' text effect based on the font size and a thickness percentage.
 *
 * @param {number} fontSize - The size of the font in pixels.
 * @param {number} thicknessPercentage - The desired thickness as a percentage.
 *
 * @returns {number} The calculated thickness of the outline.
 */
const calculateHollowThickness = (fontSize: number, thicknessPercentage: number) => {
  // adjust this to increase the thickness of the outline - by trial and error 0.0916664 seems to be a good value
  const thicknessScale = 0.0916664;
  const thickness = ((fontSize * thicknessPercentage) / 100) * thicknessScale;

  return thickness;
};

/**
 * Calculates the x and y displacements for a 'repeat' text effect based on the font size, offset, and direction.
 *
 * @param {number} fontSize - The size of the font in pixels.
 * @param {number} offset - The desired offset.
 * @param {number} direction - The direction of the effect in degrees. 0 degrees corresponds to 'up'.
 *
 * @returns {number[]} An array containing the x and y displacements for the effect.
 */
const calculateRepeatDisplacement = (fontSize: number, offset: number, direction: number) => {
  // Subtract 90 from the direction to make 0 degrees correspond to 'up'
  direction -= 90;

  // Convert the direction to radians
  const directionRadians = (direction * Math.PI) / 180;

  // Calculate the displacement
  const displacement = (fontSize * offset) / 1000;

  // Calculate the x and y displacements
  const xDisplacement = displacement * Math.cos(directionRadians);
  const yDisplacement = displacement * Math.sin(directionRadians);

  return [xDisplacement, yDisplacement];
};

/**
 * Calculates the displacement, alpha value, and spread radius for a 'glow' text effect based on the font size and an intensity value.
 *
 * @param {number} fontSize - The size of the font in pixels.
 * @param {number} intensity - The intensity of the glow effect.
 *
 * @returns {Object} An object containing the displacement, alpha value, and spread radius for the effect.
 */
const calculateGlowEffect = (fontSize: number, intensity: number) => {
  // Calculate the rgba values
  const alpha = 0.05 + intensity / 100;

  // Calculate the displacement and blur
  const displacement = fontSize / 20;
  const spreadRadius = displacement * (1 + intensity / 10);

  return { displacement, alpha, spreadRadius };
};

/**
 * Checks if a color is valid and unique.
 * If the color is valid and unique, it returns the color as an object with r, g, and b values.
 *
 * @param {number} red - The red value of the color.
 * @param {number} green - The green value of the color.
 * @param {number} blue - The blue value of the color.
 * @param {Object} colorMap - A map of all the colors that are already in use.
 *
 * @returns {Object} An object containing the r, g, and b values for the color if it is valid and unique, otherwise null.
 */
const checkNewColor = (red: number, green: number, blue: number, colorMap: { [key: string]: boolean }) => {
  if (red >= 0 && red <= 255 && green >= 0 && green <= 255 && blue >= 0 && blue <= 255) {
    const newColor = `rgb(${red}, ${green}, ${blue})`;
    if (!colorMap[newColor]) {
      return { r: red, g: green, b: blue };
    }
  }
  return null;
};

/**
 * Generates a new color for a shadow effect based on the current color and a map of all the colors that are already in use.
 * The function checks nearby colors of the current rgb color and returns the first valid and unique color it finds.
 *
 * If a valid new color is found, it is returned as an object with r, g, and b values.
 *
 * @param {Object} colorMap - A map of all the colors that are already in use.
 * @param {Object} currentColor - The current color.
 */
const generateClosestShadowColor = (
  colorMap: { [key: string]: boolean },
  currentColor: { r: number; g: number; b: number },
) => {
  const { r, g, b } = currentColor;

  // Check nearby colors of the current rgb color
  for (let i = -1; i <= 1; i++) {
    let newColor;

    // Check r,g,b
    newColor = checkNewColor(r + i, g, b, colorMap);
    if (newColor) return newColor;
    newColor = checkNewColor(r, g + i, b, colorMap);
    if (newColor) return newColor;
    newColor = checkNewColor(r, g, b + i, colorMap);
    if (newColor) return newColor;
  }

  // No valid new color found
  // Return the original color
  return currentColor;
};

/**
 * Returns the shadow color for a text effect based off the primary color.
 *
 * @param {string} primaryColor - The primary color of the text.
 * @param {boolean} shouldSetUniqueShadowColor - Whether or not the shadow color should be unique (as in this color is not set as a text color for other textbased widgets)
 *
 * @returns {Object} An object containing the r, g, and b values for the shadow color.
 */
const getEffectShadowColor = (primaryColor: string, shouldSetUniqueShadowColor: boolean) => {
  const rgbObject = parseColorToRgba(primaryColor);

  if (!shouldSetUniqueShadowColor) return rgbObject;

  // Sets a unique shadow color
  // For exporting the shadow color has to be unique to prevent PDF rendering issues
  const allTextColorMap = getAllTextBasedWidgetColors();
  if (allTextColorMap[`rgb(${rgbObject.r}, ${rgbObject.g}, ${rgbObject.b})`]) {
    return generateClosestShadowColor(allTextColorMap, rgbObject);
  }

  return rgbObject;
};

/**
 * Computes and returns the necessary CSS attributes to achieve various text effects.
 * Optimizes the effect by removing the shadow effects if certain conditions are met.
 *
 * @param {Effect} effect - The effect to apply. Should be an object that can contain keys for 'type', 'blur', 'transparency', 'offset', 'intensity', 'thickness', 'direction', 'primaryColor'.
 * @param {string} color - The color of the text.
 * @param {number} fontSize - The size of the font in pixels.
 * @param {boolean} shouldSetUniqueShadowColor - Whether or not the shadow color should be unique - e.g. for exporting it should be unique to prevent PDF rendering issues.
 * @param {boolean} optimize - Whether or not to optimize the effect by removing shadow effects under certain conditions.
 *
 * @returns {Object} An object containing the CSS attributes for the desired effect.
 */
const getOptimizedEffect = ({
  effect,
  color,
  fontSize,
  shouldSetUniqueShadowColor = false,
  optimize,
}: {
  effect: Effect;
  color: string;
  fontSize: number;
  shouldSetUniqueShadowColor?: boolean;
  optimize: boolean;
}) => {
  const {
    type = DEFAULT_EFFECT.type,
    transparency = DEFAULT_EFFECT.transparency,
    offset = DEFAULT_EFFECT.offset,
    intensity = DEFAULT_EFFECT.intensity,
  } = effect;

  if (optimize) {
    // If text effect can barely be seen in pm slides, then don't show it in the slides
    if (
      (type === 'shadow' && (fontSize < 26 || (transparency <= 40 && fontSize < 64))) ||
      (type === 'glow' && intensity < 75) ||
      (type === 'offset' && (fontSize < 26 || (transparency <= 40 && fontSize < 64))) ||
      (type === 'repeat' && (fontSize < 26 || offset <= 90))
    ) {
      return {};
    }
  }

  return getEffect({ effect, color, fontSize, shouldSetUniqueShadowColor });
};

/**
 * Computes and returns the necessary CSS attributes to achieve various text effects.
 *
 * @param {Effect} effect - The effect to apply. Should be an object that can contain keys for 'type', 'blur', 'transparency', 'offset', 'intensity', 'thickness', 'direction', 'primaryColor'.
 * @param {string} color - The color of the text.
 * @param {number} fontSize - The size of the font in pixels.
 * @param {boolean} shouldSetUniqueShadowColor - Whether or not the shadow color should be unique - e.g. for exporting it should be unique to prevent PDF rendering issues.
 *
 * @returns {Object} An object containing the CSS attributes for the desired effect.
 */
const getEffect = ({
  effect,
  color,
  fontSize,
  shouldSetUniqueShadowColor = false,
}: {
  effect: Effect;
  color: string;
  fontSize: number;
  shouldSetUniqueShadowColor?: boolean;
}) => {
  const {
    type = DEFAULT_EFFECT.type,
    blur = DEFAULT_EFFECT.blur,
    transparency = DEFAULT_EFFECT.transparency,
    offset = DEFAULT_EFFECT.offset,
    intensity = DEFAULT_EFFECT.intensity,
    thickness = DEFAULT_EFFECT.thickness,
    direction = DEFAULT_EFFECT.direction,
    primaryColor = DEFAULT_EFFECT.primaryColor,
  } = effect;

  if (!type) return {};

  if (type === 'shadow') {
    const { xOffset, yOffset } = calculateShadowDirection(fontSize, offset, direction);
    const shadowBlur = calculateShadowBlur(fontSize, blur);
    const shadowColor = getEffectShadowColor(primaryColor, shouldSetUniqueShadowColor);

    const textShadow = `rgb(${shadowColor.r} ${shadowColor.g} ${shadowColor.b} / ${transparency}%) ${xOffset}px ${yOffset}px ${shadowBlur}px`;

    return { textShadow };
  }

  if (type === 'glow') {
    const { displacement, alpha, spreadRadius } = calculateGlowEffect(fontSize, intensity);
    const shadowColor = getEffectShadowColor(primaryColor, shouldSetUniqueShadowColor);
    const textShadow = `rgba(${shadowColor.r}, ${shadowColor.g}, ${shadowColor.b}, ${alpha}) 0px ${displacement}px ${spreadRadius}px`;

    return { textShadow };
  }

  if (type === 'outline') {
    const hollowThickness = calculateHollowThickness(fontSize, thickness);
    const rgb = toRgbString(color);

    return { WebkitTextStroke: `${hollowThickness}px ${rgb}`, WebkitTextFillColor: 'transparent' };
  }

  if (type === 'offset') {
    const { xOffset, yOffset } = calculateShadowDirection(fontSize, offset, direction);
    const shadowBlur = calculateShadowBlur(fontSize, blur);
    const { r, g, b } = parseColorToRgba(primaryColor);
    const textShadow = `rgb(${r} ${g} ${b} / ${transparency}%) ${xOffset}px ${yOffset}px ${shadowBlur}px`;

    return { textShadow };
  }

  if (type === 'repeat') {
    const [xDisplacement1, yDisplacement1] = calculateRepeatDisplacement(fontSize, offset, direction);
    const [xDisplacement2, yDisplacement2] = calculateRepeatDisplacement(fontSize, offset * 2, direction);
    const { r, g, b } = getEffectShadowColor(primaryColor, shouldSetUniqueShadowColor);

    const textShadow = `rgba(${r}, ${g}, ${b}, 0.5) ${xDisplacement1}px ${yDisplacement1}px 0px, rgba(${r}, ${g}, ${b}, 0.3) ${xDisplacement2}px ${yDisplacement2}px 0px`;

    return { textShadow };
  }
};

export { getEffect, getOptimizedEffect };
